/**
 * Authentication guard for Vue Router
 */
import authService from '../services/authService.js'
import { getLegacyToMaptacLocation, ensureMaptacFullPathForRedirect } from '../utils/labRoutes.js'

export function createAuthGuard() {
  return async (to, from, next) => {
    const legacy = getLegacyToMaptacLocation(to)
    if (legacy) {
      return next(legacy)
    }

    // If the leaf (terminal) route explicitly does not require auth (e.g. /maptac/auth/callback, /maptac/:labId/login)
    const leaf = to.matched[to.matched.length - 1]
    if (leaf?.meta?.requiresAuth === false) {
      // Login routes: redirect authenticated users to the app
      const isLoginRoute = to.name === 'Login' || to.name === 'LoginLab'
      if (isLoginRoute) {
        try {
          const isAuthenticated = await authService.checkAuthStatus()
          const sessionLabId = authService.getCurrentUser()?.lab_id
          const routeLabId = to.params.labId
          if (isAuthenticated) {
            const labMismatch = routeLabId && (routeLabId !== sessionLabId)
            if (!labMismatch) {
              const targetPath = sessionLabId ? `/maptac/${sessionLabId}/` : '/maptac/login'
              next({ path: targetPath })
              return
            }
          }
        } catch (e) {
          console.warn('Auth check for login redirect failed:', e)
        }
      }
      next()
      return
    }
    // Otherwise require auth if any matched record requires it
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
    if (!requiresAuth) {
      next()
      return
    }

    try {
      // Check authentication status
      const isAuthenticated = await authService.checkAuthStatus()

      // Lab-route consistency: if route has labId, session's lab_id must match
      const routeLabId = to.params.labId
      const sessionLabId = authService.getCurrentUser()?.lab_id
      const labMismatch = routeLabId && (routeLabId !== sessionLabId)
      if (isAuthenticated && labMismatch) {
        await authService.logout()
        const redir = ensureMaptacFullPathForRedirect(to.fullPath)
        const loginTarget = routeLabId
          ? { path: `/maptac/${routeLabId}/login`, query: { redirect: redir } }
          : { name: 'Login', query: { redirect: redir } }
        next(loginTarget)
        return
      }
      
      if (isAuthenticated) {
        next()
      } else {
        // User is not authenticated, redirect to login (lab-scoped if route has labId)
        const redir = ensureMaptacFullPathForRedirect(to.fullPath)
        const loginTarget = routeLabId
          ? { path: `/maptac/${routeLabId}/login`, query: { redirect: redir } }
          : { name: 'Login', query: { redirect: redir } }
        next(loginTarget)
      }
    } catch (error) {
      console.error('Auth guard error:', error)
      // On error, redirect to login (lab-scoped if route has labId)
      const routeLabId = to.params.labId
      const redir = ensureMaptacFullPathForRedirect(to.fullPath)
      const loginTarget = routeLabId
        ? { path: `/maptac/${routeLabId}/login`, query: { redirect: redir } }
        : { name: 'Login', query: { redirect: redir } }
      next(loginTarget)
    }
  }
}
